import axios from "axios";
import classNames from "classnames";
import type {
  GetServerSideProps,
  InferGetServerSidePropsType,
  NextPage,
} from "next";
import Head from "next/head";
import React from "react";
import { v4 } from "uuid";
import Advertisement from "../../../components/Advertisement";
import ArticleTag from "../../../components/Article/ArticleTag";
import ArticleItem from "../../../components/ArticleItem";
import Container from "../../../components/Container";
import Footer from "../../../components/Footer";
import PrimaryGrid from "../../../components/Grid/PrimaryGrid";
import SecondaryGrid from "../../../components/Grid/SecondaryGrid";
import { Media } from "../../../components/Media";
import Navigation from "../../../components/Navigation";
import HistoryTableRow from "../../../components/pages/tranare/HistoryTableRow";
import ProfileTable from "../../../components/pages/tranare/ProfileTable";
import ProfileImage from "../../../components/ProfileImage";
import ReadMore from "../../../components/ReadMore";
import SectionHeader from "../../../components/SectionHeader";
import StickySidebar from "../../../components/StickySidebar";
import Table from "../../../components/Table/Table";
import TableBody from "../../../components/Table/TableBody";
import TableHeaders from "../../../components/Table/TableHeaders";
import AdsIds from "../../../data/ads";
import useBreakpoint from "../../../hooks/useBreakpoint";
import { listByIds } from "../../../services/api/ads";
import { getClub } from "../../../services/api/clubs";
import {
  getManager,
  getManagerHistory,
  getManagerNews,
} from "../../../services/api/manager";
import { API } from "../../../services/api/types";
import { AdsContext } from "../../../utils/AdsContext";
import { DeviceTypeProvider } from "../../../utils/deviceTypeContext";
import { createServerSidePropsError } from "../../../utils/errorHandling/createServerSidePropsError";
import { logError } from "../../../utils/errorHandling/logError";
import { DeviceType, getDeviceType } from "../../../utils/getDeviceType";
import slugify from "../../../utils/slugify";
import useCanonicalUrl from '../../../hooks/useCanonicalUrl';
import VideoPlayer from '../../../components/VideoPlayer';

type GetServerSidePropsParams = {
  slug: string;
  id: string;
};

export const getServerSideProps: GetServerSideProps<
  {
    manager?: API.Manager;
    news?: API.Article[];
    history?: API.ManagerHistory[];
    currentClub?: API.Club | null;
    alternativeClub?: API.Club | null;
    managerImageSrc?: string;
    ads?: API.Ad[];
    deviceType?: DeviceType;
  },
  GetServerSidePropsParams
> = async (context) => {
  try {
    const { slug, id } = context.params!;
    const [manager, news, history, ads] = await Promise.all([
      getManager(id),
      getManagerNews(id),
      getManagerHistory(id),
      listByIds([
        AdsIds.panorama_1,
        AdsIds.panorama_2,
        AdsIds.mobile_1,
        AdsIds.mobile_2,
        AdsIds.tws_1,
        AdsIds.tws_2,
        AdsIds.desktop_vi,
        AdsIds.mobile_vi,
        AdsIds.full_page_desktop,
        AdsIds.full_page_mobile,
      ]),
    ]);

    if (!manager.data?.id) {
      context.res.statusCode = 404;
      
      return createServerSidePropsError(404)
    }

    const currentClub = manager.data?.clubsId
      ? await getClub(String(manager.data.clubsId))
      : null;

    const alternativeClub = manager.data?.clubsId2
      ? await getClub(String(manager.data.clubsId2))
      : null;

    let managerImageRes = null;
    const imgSrc = `${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${manager.data.imageLarge}`;
    try {
      managerImageRes = await axios.get(imgSrc);
    } catch (err) {}
    const managerImageSrc =
      managerImageRes?.status === 200 ? imgSrc : "/profile-fallback.jpg";

    const correctSlug = slugify(manager.data.name);

    if (correctSlug !== slug)
      return {
        redirect: {
          destination: `/tranare/${correctSlug}/${id}`,
          permanent: true,
        },
      };

    return {
      props: {
        manager: manager.data,
        news: news.data,
        history: history.data,
        currentClub: currentClub?.data || null,
        alternativeClub: alternativeClub?.data || null,
        managerImageSrc,
        ads: ads.data,
        deviceType: getDeviceType(context),
      },
    };
  } catch (error: FixMeLater) {
    const errorId = v4();
    const isUserError =
      error.response?.status >= 400 && error.response?.status < 500;
    const response = createServerSidePropsError(
      isUserError ? 404 : 500,
      isUserError ? undefined : errorId
    );

    if (!isUserError) logError(error, errorId);

    context.res.statusCode = isUserError ? 404 : 500;

    return response;
  }
};

const ManagerScreen: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const isAboveSm = useBreakpoint("sm", props.deviceType!);
  const { manager, news, history, currentClub, alternativeClub, managerImageSrc } = props;

  const statTableHeaders: (
    | { text: FixMeLater; style: React.CSSProperties }
    | string
  )[] = isAboveSm
    ? [
        "",
        { text: "Klubb", style: { width: "50%" } },
        { text: "Säsong", style: { width: "50%" } },
        { text: "Placering", style: { textAlign: "center" } },
      ]
    : ["", { text: "Klubb/Säsong", style: { width: "100%" } }, "Placering"];

  const canonicalUrl = useCanonicalUrl();

  return (
    <AdsContext.Provider value={props.ads!}>
      <DeviceTypeProvider value={props.deviceType!}>
        <Media lessThan="md">
          <Advertisement id={AdsIds.full_page_mobile} />
        </Media>
        <Media greaterThanOrEqual="md">
          <Advertisement id={AdsIds.full_page_desktop} />
        </Media>

        <>
          <Head>
            <title>
              {`${manager?.name} - ${
                currentClub?.name
                  ? `Tränare för ${currentClub?.name}`
                  : "Tränare"
              } - Fotbolltransfers`}
            </title>
            <meta
              name="description"
              content={`${manager?.name} - Läs det senaste om ${manager?.name} och fotbollsnyheter hos oss på Fotbolltransfers. Vi är Sverige största sajt om fotbollsövergångar.`}
            />
            <meta property="og:image" content={managerImageSrc} />
            <link rel="canonical" href={canonicalUrl} />
          </Head>

          <Container>
            <Navigation />
          </Container>

          <Container>
            <PrimaryGrid
              top={
                <>
                  <Media greaterThanOrEqual="panorama">
                    <Advertisement id={AdsIds.panorama_1} />
                  </Media>
                  <Media lessThan="panorama">
                    <Advertisement id={AdsIds.mobile_1} />
                  </Media>
                </>
              }
              right={
                <StickySidebar>
                  <Advertisement id={AdsIds.tws_1} />
                  <div className={classNames("mb-5")} />
                  <Advertisement id={AdsIds.tws_2} />
                </StickySidebar>
              }
              main={
                <>
                  <SecondaryGrid
                    left={
                      <>
                        <ProfileImage
                          person={manager!}
                          imgSrc={managerImageSrc!}
                          type="manager"
                        />
                        <Media lessThan="md">
                          <>
                            <ProfileTable manager={manager!} />
                            <div className={classNames("mb-5")} />

                            {currentClub && (
                              <>
                                <SectionHeader label="Klubbar" />
                                <ArticleTag
                                  key={currentClub!.name}
                                  href={`/klubbar/${slugify(
                                    currentClub.countryName
                                  )}/${slugify(currentClub?.name)}/${
                                    currentClub.id
                                  }`}
                                  label={currentClub!.name}
                                  imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${currentClub?.image}`}
                                  roundImage={true}
                                  id={currentClub.id}
                                  type="club"
                                />

                                {alternativeClub && (<ArticleTag
                                  key={alternativeClub!.name}
                                  href={`/klubbar/${slugify(
                                    alternativeClub.countryName
                                  )}/${slugify(alternativeClub?.name)}/${
                                    alternativeClub.id
                                  }`}
                                  label={alternativeClub!.name}
                                  imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${alternativeClub?.image}`}
                                  roundImage={true}
                                  id={alternativeClub.id}
                                  type="club"
                                />)}
                                <div className={classNames("mb-5")} />
                              </>
                            )}
                          </>
                        </Media>

                        <Media lessThan="md">
                            <SectionHeader label="Video" />
                            <div className='py-5 min-h-[310px]'>
                              <VideoPlayer id="AAFvPwg3UsE-X5Fa" />
                            </div>
                          </Media>

                        <SectionHeader label="Historik" />
                        <Table>
                          <TableHeaders headers={statTableHeaders} />
                          <TableBody alternateRowColor={false}>
                            {history!.map((stat, index) => (
                              <HistoryTableRow
                                history={stat}
                                key={stat.id}
                                showBg={Boolean(index % 2)}
                              />
                            ))}
                          </TableBody>
                        </Table>
                        <div className={classNames("mb-5")} />

                        <Media lessThan="md">
                          <>
                            <Advertisement id={AdsIds.mobile_vi} />
                            <div className={classNames("mb-5")} />
                            <SectionHeader label="Senaste nytt" />
                            {news!.slice(0, 5).map((article) => {
                              return (
                                <div
                                  key={article.id}
                                  className={classNames([
                                    "py-5",
                                    "px-5",
                                    "border-b",
                                  ])}
                                >
                                  <ArticleItem
                                    type={"small"}
                                    article={article}
                                    ratio="16:9"
                                  />
                                </div>
                              );
                            })}
                            {news!.length > 0 ? (
                              <>
                                <ReadMore
                                  label={`Alla nyheter om ${manager!.name}`}
                                  href={`/tranare/${slugify(manager!.name)}/${
                                    manager!.id
                                  }/nyheter`}
                                />
                                <div className={classNames("mb-5")} />
                              </>
                            ) : (
                              <p
                                className={classNames([
                                  "py-5",
                                  "border-b",
                                  "p-4",
                                  "font-semibold",
                                ])}
                              >
                                Inga nyheter att visa
                              </p>
                            )}
                          </>
                        </Media>
                      </>
                    }
                    right={
                      <>
                        <SectionHeader label="Video" />
                        <div className='py-5 min-h-[290px]'>
                          <VideoPlayer id="AAFvPwg3UsE-X5Fa" />
                        </div>

                        <ProfileTable manager={manager!} />
                        <div className={classNames("mb-5")} />

                        {currentClub && (
                          <>
                            <SectionHeader label="Klubbar" />
                            <ArticleTag
                              key={currentClub!.name}
                              href={`/klubbar/${slugify(
                                currentClub.countryName
                              )}/${slugify(currentClub?.name)}/${
                                currentClub.id
                              }`}
                              label={currentClub!.name}
                              imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${currentClub?.image}`}
                              roundImage={true}
                              id={currentClub.id}
                              type="club"
                            />
                            
                            {alternativeClub && (<ArticleTag
                              key={alternativeClub!.name}
                              href={`/klubbar/${slugify(
                                alternativeClub.countryName
                              )}/${slugify(alternativeClub?.name)}/${
                                alternativeClub.id
                              }`}
                              label={alternativeClub!.name}
                              imageSrc={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${alternativeClub?.image}`}
                              roundImage={true}
                              id={alternativeClub.id}
                              type="club"
                            />)}
                            <div className={classNames("mb-5")} />
                          </>
                        )}
                        <SectionHeader label="Senaste nytt" />
                        {news!.slice(0, 5).map((article) => {
                          return (
                            <div
                              key={article.id}
                              className={classNames(["py-5", "border-b"])}
                            >
                              <ArticleItem type={"small"} article={article} />
                            </div>
                          );
                        })}
                        {news!.length > 0 ? (
                          <ReadMore
                            label={`Alla nyheter om ${manager!.name}`}
                            href={`/tranare/${slugify(manager!.name)}/${
                              manager!.id
                            }/nyheter`}
                          />
                        ) : (
                          <p
                            className={classNames([
                              "py-5",
                              "border-b",
                              "p-4",
                              "font-semibold",
                            ])}
                          >
                            Inga nyheter att visa
                          </p>
                        )}
                        <div className={classNames("mb-5")} />
                        <Advertisement id={AdsIds.desktop_vi} />
                      </>
                    }
                  />

                  <Media greaterThanOrEqual="panorama">
                    <Advertisement id={AdsIds.panorama_2} />
                  </Media>
                  <Media lessThan="panorama">
                    <Advertisement id={AdsIds.mobile_2} />
                  </Media>

                  <div className={classNames("relative", "mb-5")} />
                </>
              }
            />
            <Footer />
          </Container>
        </>
      </DeviceTypeProvider>
    </AdsContext.Provider>
  );
};

export default ManagerScreen;
